import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import {
  HelpIcon,
  EmailIcon,
  DocumentIcon,
} from "../components/icons";
import { useHotel } from "../contexts/HotelContext";
import { getDomainConfig } from "../utils/domainConfig";
import { TOUR_STORAGE_KEY_PREFIX } from "../config/onboardingTourConfig";

const Help: React.FC = () => {
  const { currentTenant } = useHotel();
  const { supportEmail: domainSupportEmail, docsUrl } = getDomainConfig();
  const [tourResetMessage, setTourResetMessage] = useState(false);
  const [tourResetError, setTourResetError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount to prevent state update on unmounted component
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  /**
   * Remove all onboarding tour localStorage keys.
   * Iterates over all keys and removes those starting with the tour prefix.
   */
  const handleResetTours = useCallback(() => {
    setTourResetError(false);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(TOUR_STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      setTourResetMessage(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setTourResetMessage(false), 3000);
    } catch (e) {
      console.warn("localStorage error during tour reset:", e);
      setTourResetError(true);
      errorTimerRef.current = setTimeout(() => setTourResetError(false), 3000);
    }
  }, []);
  const supportEmail =
    currentTenant?.brand?.replyToAddress || domainSupportEmail;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="flex items-center mb-8">
            <HelpIcon className="h-8 w-8 text-primary-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Pomoc</h1>
          </div>

          <div className="space-y-8">
            {/* Knowledge Base - shown only for brands with docs */}
            {docsUrl && (
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  Baza wiedzy
                </h2>
                <p className="text-gray-600 mb-4">
                  Odpowiedzi na najczęstsze pytania, instrukcje i poradniki
                  znajdziesz w naszej bazie wiedzy. Sprawdź ją w pierwszej
                  kolejności &mdash; większość problemów można rozwiązać
                  samodzielnie.
                </p>
                <a
                  href={docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Przejdź do bazy wiedzy (otwiera się w nowej karcie)"
                  className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <DocumentIcon className="h-5 w-5 mr-2" />
                  Przejdź do bazy wiedzy
                </a>
              </section>
            )}

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Kontakt
              </h2>
              <p className="text-gray-600 mb-4">
                {docsUrl
                  ? "Potrzebujesz dodatkowej pomocy? Napisz do nas."
                  : "Potrzebujesz pomocy? Napisz do nas."}
              </p>
              <p className="flex items-center text-lg">
                <EmailIcon className="h-5 w-5 text-primary-600 mr-2" />
                <a
                  href={`mailto:${supportEmail}`}
                  className="text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {supportEmail}
                </a>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Odpowiadamy w ciągu 24 godzin w dni robocze
              </p>
            </section>

            {/* Tutorial / Samouczek */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Samouczek
              </h2>
              <p className="text-gray-600 mb-4">
                Interaktywny tour po serwisie pomoże Ci poznać najważniejsze
                funkcje. Tour uruchamia się automatycznie przy pierwszym
                logowaniu. Możesz go uruchomić ponownie w każdej chwili.
              </p>
              <button
                onClick={handleResetTours}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Uruchom samouczek ponownie
              </button>
              {tourResetError && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  Nie udało się zresetować samouczka. Spróbuj ponownie.
                </p>
              )}
              {tourResetMessage && (
                <p className="mt-2 text-sm text-green-600" role="status">
                  Samouczek został zresetowany. Odwiedź{" "}
                  <Link to="/" className="underline font-medium">stronę główną</Link>{" "}
                  lub{" "}
                  <Link to="/grants/calls?status=OPEN" className="underline font-medium">nabory</Link>
                  , aby zobaczyć go ponownie.
                </p>
              )}
            </section>

            {/* Company Information */}
            <section className="bg-primary-50 p-6 rounded-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Informacje o firmie
              </h2>

              <div className="space-y-3 text-gray-700">
                <p className="text-lg font-medium">
                  <strong>kukla.tech sp. z o.o.</strong>
                </p>
                <p>Zawoja 1847, 34-222 Zawoja</p>
                <p>NIP: 5521724915</p>
                <p>KRS: 0000792372</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Help;
